import React, { useState } from 'react';
import { useWorkspaceDevice } from './workspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useSystemAirflow } from '../../hooks/useSystemAirflow';
import SystemAirflowSection from './SystemAirflowSection';
import FilterConfiguration from './FilterConfiguration';
import EquipmentInventorySection from './EquipmentInventorySection';
import DeviceTagsPanel from '../DeviceTagsPanel';
import ModelLink from '../ModelLink';
import EditEquipmentDrawer from './EditEquipmentDrawer';
import Button from '../ui/Button';
import { EDIT_EQUIPMENT_PERMISSION, getEquipmentDocumentFileUrl } from '../../api/equipmentEdit';
import type { EquipmentTargetKind } from '../../api/equipmentEdit';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import {
  COIL_CARD_TITLE,
  COIL_MODEL_UNCONFIRMED,
  catalogMatchLabel,
  componentReviewBadgeLabel,
  documentReferenceLabel,
  filterIdentitySourceLabel,
  indoorUnitCardTitle,
  missingFieldsLabel,
  motorConflictLabel,
  motorTypeLabel,
  outdoorUnitCardTitle,
  qualityLabel,
  ratedStaticLabel,
  routeReasonLabel,
  sourceLabel,
} from '../../utils/equipmentQuality';
import {
  coilEvidenceLabel,
  componentName,
  ductlessUnresolvedLabel,
  lowerFirstLetter,
  sameTonnageSnapshot,
  sideName,
  systemTonnageAnswer,
  tonnageConflictLabel,
  tonnageValueText,
  type TonnageAltModel,
} from '../../utils/tonnageWords';
import type { EquipmentDocumentRef, SystemTonnage, SystemTonnageAlt } from '../../types';

/**
 * Plan 94 Phase 1 - Overview tab.
 *
 * Compact device identity + responsibility path + app-context summary, plus jump
 * links into the per-device tools. Latest-reading, status, and open-alert panels
 * are intentionally left as follow-up once the workspace detail API lands.
 */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 wrap-break-word">{children}</dd>
  </div>
);

/**
 * Plan 119 — where a spec value came from, under the value. Two identical
 * tonnages can be worth very different amounts depending on which component
 * they were read off, and until Phase 1 the screen could not say so. Always
 * rendered when there is a value, so "source unknown" reads as an answer
 * rather than as a gap.
 *
 * Phase 2: the wording comes from the server's quality code, so this screen
 * and the Device List cannot describe the same value differently.
 */
const Provenance: React.FC<{ source: string | null | undefined; quality: string }> = ({
  source,
  quality,
}) => (
  <span className="mt-0.5 block text-xs text-muted-foreground" title={`Source: ${sourceLabel(source)}`}>
    {quality} <span className="text-muted-foreground">({sourceLabel(source)})</span>
  </span>
);

/**
 * Plan 119 Phase 3 (FS-627) — a homeowner's motor entry that a manufacturer
 * document contradicts, said in words rather than shown as a flag. Both sides
 * use the console's own motor names.
 *
 * The response carries the motor family the document states, so the sentence
 * names both sides. It is null when no catalog fact sits behind the conflict,
 * and then the sentence points at the document instead of quoting a value it
 * does not have. The document reference is a sibling line so each reads on
 * its own.
 */
const MotorConflict: React.FC<{
  value: string | null | undefined;
  catalogValue: string | null | undefined;
  document: EquipmentDocumentRef | null | undefined;
  locator: string | null | undefined;
}> = ({ value, catalogValue, document, locator }) => (
  <>
    <span className="mt-1 block text-xs text-red-700">
      {motorConflictLabel(value, catalogValue)}
    </span>
    <span className="block text-xs text-muted-foreground">
      {documentReferenceLabel(document, locator)}
    </span>
  </>
);

/**
 * Plan 120 — the legacy guard is holding part of this equipment record.
 *
 * A badge rather than a field, because it is a statement about the record and
 * not about any one value on it, and it sits on the indoor card because the
 * HVAC document is the record being held. The held facts are kept, never
 * deleted; an operator settles where they belong.
 */
const ComponentReviewBadge: React.FC<{ reason: string }> = ({ reason }) => (
  <span
    data-testid="component-review-badge"
    className="ml-2 rounded-sm border border-status-warning-border bg-status-warning px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-warning-foreground"
    title={routeReasonLabel(reason)}
  >
    ⚠ {componentReviewBadgeLabel(reason)}
  </span>
);

/**
 * Plan 120 — more than one coil child on this system.
 *
 * The projection carries one coil's facts, so showing them under a plain
 * heading would present one of several as the coil. The count and the badge
 * say what the record actually holds and send it to operations instead.
 */
const MultipleCoilsBadge: React.FC<{ count: number }> = ({ count }) => (
  <span
    data-testid="multiple-coils-badge"
    className="ml-2 rounded-sm border border-status-warning-border bg-status-warning px-1.5 py-0.5 text-[10px] font-semibold uppercase text-status-warning-foreground"
    title="More than one coil record is on file for this system, so no single one can be shown as the coil."
  >
    ⚠ {count} coil records — needs review
  </span>
);

/**
 * FS-838 — which record the equipment editor opens on to show a disagreeing
 * tonnage, by the side the value was read off.
 */
const drawerTabOfSide = (side: string | null | undefined): EquipmentTargetKind => {
  if (side === 'outdoor_condenser') return 'ac';
  if (side === 'indoor_coil') return 'coil';
  return 'hvac';
};

const SYSTEM_TONNAGE_MISSING_TITLE =
  'No component states one firm size. A size given only as a range (for example 3–5 t) is ' +
  'never used, and a furnace\'s "up to" size is used only when nothing else gives one.';

/**
 * FS-838 — a manufacturer document cited under a tonnage, with a button that
 * opens our stored copy. The tab is opened by the click and pointed at the
 * short-lived link afterwards, as the Equipment Catalog page does, so a popup
 * blocker does not eat it; on failure the tab is closed and the reason is
 * said in place.
 */
const DocumentEvidence: React.FC<{
  document: EquipmentDocumentRef;
  locator: string | null | undefined;
  testId: string;
  title: string;
}> = ({ document, locator, testId, title }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = async () => {
    if (document.id == null) return;
    setError(null);
    setBusy(true);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const url = await getEquipmentDocumentFileUrl(document.id);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (err) {
      tab?.close();
      setError(extractErrorMessage(err, 'no response from the device API'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="block text-xs text-muted-foreground">
      {documentReferenceLabel(document, locator)}
      {document.id != null && (
        <>
          {' '}
          <Button
            variant="link"
            data-testid={testId}
            onClick={open}
            disabled={busy}
            title={title}
          >
            {busy ? 'Opening…' : 'Open document'}
          </Button>
        </>
      )}
      {error && (
        <span className="mt-0.5 block text-xs text-red-700" role="alert">
          Could not open the document: {error}
        </span>
      )}
    </span>
  );
};

/**
 * FS-838 — the system tonnage on the indoor card: the value, which unit
 * answered and on what evidence, the document it was read out of, and any
 * credible value that disagrees. Every judgement here is the projection's;
 * this only says it.
 */
const SystemTonnageField: React.FC<{
  st: SystemTonnage;
  /**
   * `hvac_tonnage_capacity_source` from the response the winner came from;
   * `undefined` when no such response is on the page (review F1).
   */
  capacitySource: string | null | undefined;
  document: EquipmentDocumentRef | null | undefined;
  locator: string | null | undefined;
  alt: SystemTonnageAlt | null;
  altDocument: EquipmentDocumentRef | null | undefined;
  altLocator: string | null | undefined;
  /** FS-951 — the model behind the disagreeing document and the records carrying it. */
  altModel?: TonnageAltModel | null;
  /** Opens the equipment editor on the disagreeing record; null without the permission. */
  onShowAltRecord: ((side: string | null) => void) | null;
}> = ({ st, capacitySource, document, locator, alt, altDocument, altLocator, altModel, onShowAltRecord }) => {
  const code = st.quality ?? null;
  const valueTitle =
    'The system tonnage. It is taken from the furnace or air handler first, then the indoor ' +
    'coil, then the outdoor unit; CFM per ton is divided by it. ' +
    `Quality: ${qualityLabel(code)} (${code ?? 'none'}).`;
  const valueLine = (
    <span data-testid="hvac-card-tonnage" title={valueTitle}>
      {tonnageValueText(st.value, st.upper_bound)}
    </span>
  );

  if (st.value == null) {
    if (code === 'tonnage_ductless_unresolved') {
      return (
        <>
          {valueLine}
          <span data-testid="hvac-card-tonnage-ductless" className="mt-0.5 block text-xs text-red-700">
            {ductlessUnresolvedLabel(alt?.value)}
          </span>
        </>
      );
    }
    return (
      <>
        {valueLine}
        <span
          data-testid="hvac-card-tonnage-missing"
          className="mt-0.5 block text-xs text-muted-foreground"
          // An unreadable record is a different answer from "no firm size".
          title={code === 'tonnage_unavailable' ? undefined : SYSTEM_TONNAGE_MISSING_TITLE}
        >
          {qualityLabel(code)}
        </span>
      </>
    );
  }

  const altPart = sideName(alt?.side);
  return (
    <>
      {valueLine}
      <span
        data-testid="hvac-card-tonnage-answer"
        className="mt-0.5 block text-xs text-muted-foreground"
        title={`Source: ${sourceLabel(st.source)}`}
      >
        {systemTonnageAnswer(st, capacitySource)}
      </span>
      {document && (
        <DocumentEvidence
          document={document}
          locator={locator}
          testId="hvac-card-tonnage-document"
          title="Open the copy we stored of the manufacturer document this tonnage was read from, in a new tab."
        />
      )}
      {st.conflict === true && (
        <>
          <span data-testid="hvac-card-tonnage-conflict" className="mt-1 block text-xs text-red-700">
            {tonnageConflictLabel({ alt: alt?.value, altSide: alt?.side, altSource: alt?.source, altModel })}
          </span>
          {altDocument ? (
            <DocumentEvidence
              document={altDocument}
              locator={altLocator}
              testId="hvac-card-tonnage-alt-document"
              title="Open the copy we stored of the manufacturer document behind the disagreeing value, in a new tab."
            />
          ) : onShowAltRecord ? (
            <Button
              variant="link"
              size="sm"
              data-testid="hvac-card-tonnage-alt-record"
              onClick={() => onShowAltRecord(alt?.side ?? null)}
              title="Open the equipment editor on the record that holds the disagreeing value, with its photos."
              className="block"
            >
              Show the {altPart} record
            </Button>
          ) : (
            <span data-testid="hvac-card-tonnage-alt-record" className="block text-xs text-muted-foreground">
              On the {altPart} record.
            </span>
          )}
        </>
      )}
    </>
  );
};

/**
 * FS-838 — what the coil's own tonnage has to do with the system's. The coil
 * card used to borrow the system's quality rung, which described another
 * unit's value whenever the coil was not the one answering.
 */
const coilSystemRole = (
  st: SystemTonnage | null,
  coilTonnage: number | string | null | undefined,
  capacitySource: string | null | undefined,
): string | null => {
  const coilHasValue = coilTonnage !== null && coilTonnage !== undefined && coilTonnage !== '';
  if (st && st.value != null && st.component === 'indoor_coil') {
    if (coilHasValue) return 'This is the system tonnage.';
    return `The system tonnage (${tonnageValueText(st.value, st.upper_bound)}) comes from this coil: ` +
      `${lowerFirstLetter(systemTonnageAnswer(st, capacitySource))}.`;
  }
  if (coilHasValue && st && st.value != null) {
    return `Not the system tonnage: the ${componentName(st.component)} gives the size first.`;
  }
  return null;
};

/** "none" reads better than "0", and an absent record is not the same as a record with no photo. */
const photoCount = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return 'no record';
  return n === 0 ? 'none' : String(n);
};

const OverviewTab: React.FC = () => {
  const { device, refetchDevice } = useWorkspaceDevice();
  const { hasPermission } = useAuth();
  const appCtx = device.app_context;
  const hasAppContext = !!appCtx && appCtx.status !== 'missing';
  // Plan 33 Phase 6: multi-Clip system airflow.
  //
  // FS-593: fetched for every device, not only app-registered ones — the
  // filter configuration block is drawn outside the app-context gate, and a
  // device the projection has not reached is exactly the one that must say
  // "not synced from the app yet" rather than show nothing.
  const {
    data: systemAirflow,
    loading: systemAirflowLoading,
    error: systemAirflowError,
    refresh: systemAirflowRefresh,
    refetch: refetchSystemAirflow,
    refreshAfterSave,
    projectionStamp,
  } = useSystemAirflow(device.id, {
    // FS-593: once the projection has caught up with a save, the device
    // detail is read once more so the rest of the page matches the block.
    onSynced: () => refetchDevice?.(),
  });
  const filterConfiguration = (
    <FilterConfiguration
      data={systemAirflow}
      loading={systemAirflowLoading}
      error={systemAirflowError}
      deviceId={device.id}
      refresh={systemAirflowRefresh}
      // FS-952: the drawer no longer reloads the device on a timer, so the
      // manual refresh after a poll that gave up reads the device too.
      onRefresh={() => {
        refetchSystemAirflow();
        refetchDevice?.();
      }}
    />
  );
  // FS-593: the drawer names another Clip's slot by serial; this is how it
  // links to that Clip's page when the system response says which device it is.
  const clipLinks: Record<string, number> = {};
  (systemAirflow?.clips ?? []).forEach((clip) => {
    if (clip.name) clipLinks[clip.name.toLowerCase()] = clip.device_id;
  });
  (systemAirflow?.slots ?? []).forEach((slot) => {
    if (slot.device_name && slot.device_id != null) {
      clipLinks[slot.device_name.toLowerCase()] = slot.device_id;
    }
  });
  // FS-566: the tonnage the server selected for the whole system. The card
  // takes it from the SAME airflow response the CFM/ton headline reads, so
  // the two refresh together (after an edit the airflow refetch is immediate
  // while the device reload is not) and cannot name different tonnages.
  // Only when that response is absent does the device detail's copy stand
  // in; an API that predates both leaves the card as it was.
  const systemTonnage = systemAirflow?.tonnage ?? appCtx?.system_tonnage ?? null;
  // FS-502: correcting a homeowner's own records is its own permission, and an
  // operator without it sees exactly the page that was here before. The button
  // needs an app registration to point at, so a device with none never has it.
  const canEditEquipment = hasPermission(EDIT_EQUIPMENT_PERMISSION) && hasAppContext;
  // FS-838: the editor can also be opened on the record behind a disagreeing
  // tonnage, with the cursor in its tonnage box.
  const [editingEquipment, setEditingEquipment] = useState<
    { tab?: EquipmentTargetKind; field?: string } | null
  >(null);
  // FS-838 review F2: the documents, locators and the capacity source come
  // off the device detail, which can be an older read than the airflow
  // response the winner is taken from. They are used only when the detail's
  // own system tonnage is the same snapshot as the one on screen; otherwise
  // they are withheld until the reads agree, never shown under a winner or a
  // disagreement they do not belong to.
  const detailTonnage = appCtx?.system_tonnage ?? null;
  const detailMatches = sameTonnageSnapshot(systemTonnage, detailTonnage);
  // F1/F7: the winner's capacity source from the read that supplied the
  // winner: the detail's own column when the detail supplied it (or the two
  // reads are proven the same snapshot); otherwise only what the displayed
  // snapshot carries itself, else "source not recorded".
  const tonnageCapacitySource = detailMatches && appCtx
    && Object.prototype.hasOwnProperty.call(appCtx, 'hvac_tonnage_capacity_source')
    ? appCtx.hvac_tonnage_capacity_source ?? null
    : undefined;
  // The disagreeing value: the system tonnage's own, or — for a system object
  // that predates FS-835 — the same snapshot's detail columns.
  const tonnageAlt: SystemTonnageAlt | null =
    systemTonnage && Object.prototype.hasOwnProperty.call(systemTonnage, 'alt')
      ? systemTonnage.alt ?? null
      : detailMatches && appCtx?.hvac_tonnage_alt != null
        ? {
          value: appCtx.hvac_tonnage_alt,
          side: appCtx.hvac_tonnage_alt_side ?? null,
          source: appCtx.hvac_tonnage_alt_source ?? null,
          document_id: appCtx.hvac_tonnage_alt_document_id ?? null,
        }
        : null;
  const sameId = (a: unknown, b: unknown): boolean => a != null && b != null && String(a) === String(b);
  const tonnageDocument = detailMatches
    && (systemTonnage?.document_id == null || sameId(systemTonnage.document_id, appCtx?.hvac_tonnage_document?.id))
    ? appCtx?.hvac_tonnage_document ?? null
    : null;
  // The disagreement's document must be the one the displayed alt names.
  const tonnageAltDocument = detailMatches && sameId(appCtx?.hvac_tonnage_alt_document?.id, tonnageAlt?.document_id)
    ? appCtx?.hvac_tonnage_alt_document ?? null
    : null;
  // FS-951: the document's model, from the same detail read, under the same rule.
  const tonnageAltModel = tonnageAltDocument ? appCtx?.hvac_tonnage_alt_model ?? null : null;
  // Plan 120 — the coil is a component of its own, so the cards are named
  // after the machines they describe rather than after the documents they
  // came out of. `coil_count` is what says there is a coil to draw: a coil
  // child with no model number is still a coil on file.
  const coilCount = appCtx?.coil_count ?? 0;
  const indoorCardTitle = indoorUnitCardTitle({
    catalogEquipmentType: appCtx?.hvac_catalog_equipment_type,
    model: appCtx?.hvac_model,
    coilCount,
  });
  const componentReviewReason = appCtx?.hvac_component_review_reason ?? null;
  const coilRole = coilSystemRole(systemTonnage, appCtx?.coil_tonnage, tonnageCapacitySource);

  return (
    <div className="bg-card border border-border rounded-lg shadow-md p-6 space-y-6">
      <section>
        <h3 className="text-lg font-semibold mb-3">Identity</h3>
        <dl className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
          <Field label="RDS id">
            <span className="font-mono">{device.id}</span>
          </Field>
          <Field label="Serial / name">
            <span className="font-mono break-all">{device.name}</span>
          </Field>
          {/* FS-637: assembly variant, set in the Device List edit modal. */}
          <Field label="Assy">{device.assy || '—'}</Field>
          <Field label="Description">{device.description || '—'}</Field>
          <Field label="Responsibility path">
            <span className="font-mono break-all">{device.entity_path || '—'}</span>
          </Field>
          <Field label="Created">{device.created_date?.slice(0, 10) || '—'}</Field>
          <Field label="Calibration offset">{device.calibration_offset} Pa</Field>
        </dl>
        {/* FS-501: tags sit under Identity because that is what they are —
            how this unit is classified, visible at a glance. Ship-to detail
            and history stay in Notes. */}
        <div className="mt-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Tags</p>
          <DeviceTagsPanel deviceId={device.id} />
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">
          MyFluxHive app registration
          {appCtx?.status === 'ambiguous' && (
            <span className="ml-2 text-xs text-status-warning-foreground">⚠ duplicate registrations</span>
          )}
          {appCtx?.status === 'stale' && (
            <span className="ml-2 text-xs text-status-warning-foreground">⚠ stale app data</span>
          )}
        </h3>
        {!hasAppContext ? (
          <p className="text-sm text-muted-foreground">
            This device is not registered in the MyFluxHive app (logger or unregistered device).
          </p>
        ) : (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Owner">
              {appCtx?.owner_email || appCtx?.owner_name || '—'}
              {appCtx?.owner_email && appCtx.owner_name && <span className="block text-xs text-muted-foreground">{appCtx.owner_name}</span>}
            </Field>
            <Field label="Email">{appCtx!.owner_email || '—'}</Field>
            <Field label="Location">{appCtx!.location_name || '—'}</Field>
            <Field label="HVAC">{appCtx!.hvac_name || '—'}</Field>
            {/* Plan 119 Phase 4: the filter had no provenance at all until
                now — the catalog match state was the only quality signal
                there was. Two lines under the summary, in the same style the
                spec values use: who said what the filter is, and what the
                catalog match behind its curve was worth. */}
            <Field label="Filter">
              {appCtx!.filter_summary || '—'}
              {appCtx!.filter_summary && (
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  {filterIdentitySourceLabel(appCtx!.filter_identity_source)}
                  {appCtx!.filter_model && (
                    <span className="text-muted-foreground"> · model {appCtx!.filter_model}</span>
                  )}
                </span>
              )}
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {catalogMatchLabel({
                  catalogId: appCtx!.filter_catalog_id,
                  source: appCtx!.filter_catalog_source,
                  confidence: appCtx!.filter_catalog_confidence,
                  sizeVerified: appCtx!.filter_catalog_size_verified,
                  matchedAt: appCtx!.filter_catalog_matched_at,
                })}
              </span>
            </Field>
          </dl>
        )}
      </section>

      {hasAppContext && (
        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            {/* Plan 120: the machine, not the document. "Furnace", "Air
                handler" or "Packaged unit" only when the catalog resolved one
                of those; anything else says so rather than implying we know.
                The blower and motor fields stay on this card, because they
                belong to the unit that has a blower — never to the coil. */}
            <h3 className="text-lg font-semibold">
              {indoorCardTitle}
              {componentReviewReason && <ComponentReviewBadge reason={componentReviewReason} />}
            </h3>
            {canEditEquipment && (
              <Button
                variant="primary"
                onClick={() => setEditingEquipment({})}
                title="Correct the equipment and filter records on the homeowner's app account."
              >
                Edit equipment
              </Button>
            )}
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Brand">{appCtx!.hvac_manufacturer || '—'}</Field>
            <Field label="Model">
              {/* Into the Equipment Catalog: at the listing when the record
                  matched one, at the catalog search when it did not — which
                  is how an operator finds out we hold nothing for it. */}
              <ModelLink
                model={appCtx!.hvac_model}
                catalogModelId={appCtx!.hvac_catalog_model_id}
                className="font-mono break-all"
              />
            </Field>
            {/* FS-838: "System", because the field sits on the indoor card
                while the value may come off the coil or the outdoor unit. */}
            <Field label="System tonnage">
              {systemTonnage ? (
                <SystemTonnageField
                  st={systemTonnage}
                  capacitySource={tonnageCapacitySource}
                  document={tonnageDocument}
                  locator={tonnageDocument ? appCtx!.hvac_tonnage_locator : null}
                  alt={tonnageAlt}
                  altDocument={tonnageAltDocument}
                  altLocator={tonnageAltDocument ? appCtx!.hvac_tonnage_alt_locator : null}
                  altModel={tonnageAltModel}
                  onShowAltRecord={canEditEquipment
                    ? (side) => setEditingEquipment({ tab: drawerTabOfSide(side), field: 'tonnage' })
                    : null}
                />
              ) : (
                <>
                  {appCtx!.hvac_tonnage != null ? `${appCtx!.hvac_tonnage} t` : '—'}
                  {appCtx!.hvac_tonnage != null && (
                    <Provenance
                      source={appCtx!.hvac_tonnage_source}
                      quality={qualityLabel(appCtx!.hvac_tonnage_quality)}
                    />
                  )}
                </>
              )}
            </Field>
            <Field label="BTU">
              {appCtx!.hvac_btu_rating != null
                ? appCtx!.hvac_btu_rating.toLocaleString()
                : '—'}
            </Field>
            <Field label="Motor type">
              {appCtx!.hvac_blower_type ? motorTypeLabel(appCtx!.hvac_blower_type) : '—'}
              {appCtx!.hvac_blower_type && (
                <Provenance
                  source={appCtx!.hvac_blower_type_source}
                  quality={qualityLabel(appCtx!.hvac_blower_type_quality)}
                />
              )}
              {appCtx!.hvac_blower_type_conflict === true && (
                <MotorConflict
                  value={appCtx!.hvac_blower_type}
                  catalogValue={appCtx!.hvac_blower_type_catalog_value}
                  document={appCtx!.hvac_blower_type_document}
                  locator={appCtx!.hvac_blower_type_locator}
                />
              )}
            </Field>
            {/* Plan 119 Phase 3 (FS-627): the manufacturer's published
                operating point for this model, out of the equipment catalog.
                Worded by the same formatter the Device List uses, and it says
                why when there is none rather than leaving a blank. */}
            <Field label="Rated external static pressure">
              {ratedStaticLabel({
                pa: appCtx!.hvac_rated_esp_pa,
                original: appCtx!.hvac_rated_esp_original,
                kind: appCtx!.hvac_rated_esp_kind,
                document: appCtx!.hvac_rated_esp_document ?? null,
                locator: appCtx!.hvac_rated_esp_locator,
                catalogModelId: appCtx!.hvac_catalog_model_id,
              })}
            </Field>
            {/* Plan 119 Phase 2 (FS-460): what this system still needs, in
                the same words the Device List and the inventory use. FS-752:
                each with its reason, as the Device List cell prints it. */}
            <Field label="Missing info">
              {missingFieldsLabel(appCtx!.missing_fields, {
                tonnage: appCtx!.hvac_tonnage_quality,
                indoor_record: appCtx!.indoor_record_quality,
                motor_type: appCtx!.hvac_blower_type_quality,
                filter_record: appCtx!.filter_quality,
              })}
            </Field>
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">
            Read from a label photo of the equipment, decoded from the model
            number, or taken from the manufacturer's document. Each value is
            resolved on its own. The system tonnage is taken from the furnace or
            air handler first, then the indoor coil, then the outdoor unit, and
            the line under it names the unit that gave it.
            Photos on file: {photoCount(appCtx!.hvac_photo_count)} for the HVAC
            system, {photoCount(appCtx!.ac_photo_count)} for the AC system,{' '}
            {photoCount(appCtx!.filter_photo_count)} for the filter.
          </p>
          {systemAirflow && (
            <SystemAirflowSection data={systemAirflow} deviceId={device.id} />
          )}
          {filterConfiguration}
        </section>
      )}

      {/* FS-593: outside the app-context gate. A device with no app context
          still gets the block, which says it is not synced yet. */}
      {!hasAppContext && <section>{filterConfiguration}</section>}

      {/* Plan 120 — the evaporator coil, as its own component.
          Drawn whenever a coil child is on file, model or no model: a coil
          nobody has read the label of is still a coil, and saying so is how
          an operator knows the tonnage above came off one. Nothing about a
          blower appears here, because a coil has none. */}
      {hasAppContext && coilCount > 0 && (
        <section data-testid="coil-card">
          <h3 className="text-lg font-semibold mb-3">
            {COIL_CARD_TITLE}
            {coilCount > 1 && <MultipleCoilsBadge count={coilCount} />}
          </h3>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Brand">{appCtx!.coil_manufacturer || '—'}</Field>
            <Field label="Model">
              {appCtx!.coil_model ? (
                <ModelLink
                  model={appCtx!.coil_model}
                  catalogModelId={appCtx!.coil_catalog_model_id}
                  className="font-mono break-all"
                />
              ) : (
                <span className="text-muted-foreground">{COIL_MODEL_UNCONFIRMED}</span>
              )}
            </Field>
            <Field label="Tonnage">
              {appCtx!.coil_tonnage != null ? `${appCtx!.coil_tonnage} t` : '—'}
              {/* FS-838: the coil's own evidence. The system's quality rung
                  described whichever unit answered, which was often not this
                  coil. */}
              {appCtx!.coil_tonnage != null && (
                <span data-testid="coil-card-tonnage-evidence" className="mt-0.5 block text-xs text-muted-foreground">
                  {coilEvidenceLabel({
                    source: appCtx!.coil_tonnage_source,
                    capacitySource: appCtx!.coil_capacity_source,
                  })}
                </span>
              )}
              {coilRole && (
                <span data-testid="coil-card-system-role" className="mt-0.5 block text-xs text-muted-foreground">
                  {coilRole}
                </span>
              )}
            </Field>
          </dl>
          <p className="mt-2 text-xs text-muted-foreground">
            A coil has no blower, so the motor type and the rated static
            pressure belong to the {indoorCardTitle.toLowerCase()} card above.
            When no furnace-blower or air-handler size is on file, the coil's
            size is the system tonnage, ahead of the outdoor unit's.
            Photos on file: {photoCount(appCtx!.coil_photo_count)}.
          </p>
        </section>
      )}

      {hasAppContext && (appCtx!.ac_model || appCtx!.ac_name) && (
        <section>
          {/* Plan 120: "Outdoor unit", plus the catalog's type when it has
              one — a condenser and a heat pump are different machines and the
              record often does not say which it is. */}
          <h3 className="text-lg font-semibold mb-3">
            {outdoorUnitCardTitle(appCtx!.ac_catalog_equipment_type)}
          </h3>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <Field label="Name">{appCtx!.ac_name || '—'}</Field>
            <Field label="Brand">{appCtx!.ac_manufacturer || '—'}</Field>
            <Field label="Model">
              <ModelLink
                model={appCtx!.ac_model}
                catalogModelId={appCtx!.ac_catalog_model_id}
                className="font-mono break-all"
              />
            </Field>
            <Field label="SEER">
              {appCtx!.ac_seer != null ? appCtx!.ac_seer : '—'}
            </Field>
            <Field label="Compressor">{appCtx!.ac_compressor_stages || '—'}</Field>
          </dl>
        </section>
      )}

      {/* Below the equipment fields, because it is the working detail behind
          them: every field the inventory tracks for this device, what it is
          worth, and the document it was read out of. Not gated on an app
          registration — the inventory is keyed on the device, and an
          unregistered Clip has rows saying exactly that. */}
      <EquipmentInventorySection deviceId={device.id} />

      {editingEquipment && (
        <EditEquipmentDrawer
          device={device}
          initialTab={editingEquipment.tab}
          focusField={editingEquipment.field}
          onClose={() => setEditingEquipment(null)}
          clipLinks={clipLinks}
          // FS-566 / FS-593: an equipment edit can change the tonnage (and
          // CFM/ton) or a filter slot, and the projection catches up through
          // the sync queue, so the system block polls until the server's
          // projection stamp moves past the one on screen when the write went,
          // and the device is read again each time it moves (FS-952: the
          // only reload; the drawer has no timed ones for a page that follows).
          projectionStamp={projectionStamp}
          onWritten={refreshAfterSave}
        />
      )}
    </div>
  );
};

export default OverviewTab;
